import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { createDecartClient, models } from '@decartai/sdk';
import './Card.css';

const DEFAULT_PROMPT = "Transform into this character";

export default function CharacterSwap() {
  const { token, hasApiKey, onUpdate } = useOutletContext();
  const [status, setStatus] = useState('Idle');
  const [streaming, setStreaming] = useState(false);
  const [referenceImageUrl, setReferenceImageUrl] = useState(null);
  const inputVideoRef = useRef(null);
  const outputVideoRef = useRef(null);
  const clientRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const handleReferenceSelected = (e) => setReferenceImageUrl(e.detail);
    window.addEventListener('reference-selected', handleReferenceSelected);
    return () => window.removeEventListener('reference-selected', handleReferenceSelected);
  }, []);

  const startSwap = async () => {
    if (!referenceImageUrl) {
      setStatus('Please select a reference image first');
      return;
    }
    setStatus('Requesting camera...');
    try {
      // Request camera FIRST (must be triggered by user click)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { width: 720, height: 1280, frameRate: 20 }
      });
      inputVideoRef.current.srcObject = stream;
      streamRef.current = stream;

      setStatus('Connecting to AI...');

      const tokenRes = await fetch('/api/realtime-token', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!tokenRes.ok) throw new Error(await tokenRes.text());
      const { apiKey } = await tokenRes.json();

      const imageBlob = await fetch(referenceImageUrl).then(r => r.blob());
      const imageFile = new File([imageBlob], 'reference.jpg', { type: imageBlob.type || 'image/jpeg' });

      const model = models.realtime('lucy_2_rt');
      const client = createDecartClient({ apiKey });
      clientRef.current = await client.realtime.connect(stream, {
        model,
        onRemoteStream: (outStream) => outputVideoRef.current.srcObject = outStream,
        initialState: {
          image: imageFile,
          prompt: { text: DEFAULT_PROMPT, enhance: true }
        }
      });
      clientRef.current.on('connectionChange', (s) => setStatus(`Status: ${s}`));
      clientRef.current.on('error', (e) => setStatus(`Error: ${e.message}`));
      setStreaming(true);
    } catch (err) {
      console.error('Camera/start error:', err);
      if (err.name === 'NotAllowedError') {
        setStatus('Camera access denied. Please allow camera and microphone access.');
      } else if (err.name === 'NotFoundError') {
        setStatus('No camera found. Please connect a camera.');
      } else {
        setStatus(`Failed: ${err.message}`);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      inputVideoRef.current.srcObject = null;
    }
  };

  const stopSwap = () => {
    if (clientRef.current) clientRef.current.disconnect();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    inputVideoRef.current.srcObject = null;
    outputVideoRef.current.srcObject = null;
    setStreaming(false);
    setStatus('Disconnected');
  };

  const openOBS = () => {
    window.open('/obs', '_blank', 'width=800,height=600');
  };

  return (
    <div className="card">
      <div className="card-title">
        <i className="fas fa-user-astronaut"></i> Character Swap
      </div>
      <div className="reference-preview">
        <strong>Selected Reference Image:</strong>
        <div className="preview-box">
          {referenceImageUrl ? (
            <img src={referenceImageUrl} alt="Reference" />
          ) : (
            <div className="preview-placeholder">None</div>
          )}
        </div>
      </div>
      <div className="card-buttons">
        <button className="btn-primary" onClick={startSwap} disabled={!hasApiKey || !referenceImageUrl || streaming}>Start Realtime</button>
        <button onClick={stopSwap} disabled={!streaming}>Stop</button>
        <button onClick={openOBS} disabled={!streaming}>Connect to OBS</button>
      </div>
      <div className="video-row">
        <div className="video-box output-main"><p>Output (Transformed)</p><video ref={outputVideoRef} autoPlay playsInline></video></div>
        <div className="video-box input-small"><p>Input (Webcam)</p><video ref={inputVideoRef} autoPlay muted playsInline></video></div>
      </div>
      <div className="status-badge">{status}</div>
    </div>
  );
}
